<template>
    <div class="min-h-screen bg-gradient-to-br from-slate-100 via-white to-cyan-50 px-4 py-8">
        <div class="max-w-lg mx-auto bg-white px-6 py-8 shadow-xl rounded-2xl border border-white/80">
            <img src="../assets/logo.png" class="pr-6 pb-6 mx-auto w-48">

            <div v-if="loading" class="text-center text-sm text-gray-400 py-8">
                Verificando link...
            </div>

            <div v-else-if="error" class="text-center py-6">
                <p class="text-red-500 font-semibold text-sm mb-2">{{ errorMessage }}</p>
                <router-link class="text-sm text-cyan-500 hover:underline" :to="{ name: 'forgotPassword' }">Solicitar novo link</router-link>
            </div>

            <div v-else-if="success" class="text-center py-6">
                <p class="text-emerald-600 font-semibold text-sm mb-2">Senha redefinida com sucesso!</p>
                <p class="text-gray-500 text-sm mb-4">Faça login com sua nova senha.</p>
                <router-link class="text-sm text-cyan-500 hover:underline" :to="{ name: 'login' }">Ir para o login</router-link>
            </div>

            <div v-else>
                <div class="text-center mb-6">
                    <h2 class="text-xl font-semibold text-gray-800 mb-1">Redefinir senha</h2>
                    <p class="text-xs text-gray-400 mt-2">{{ resetEmail }}</p>
                </div>

                <form @submit.prevent="submit" novalidate class="flex flex-col gap-y-4">
                    <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Nova senha</label>
                        <input v-model="form.password" type="password" maxlength="80" class="input input-bordered w-full text-sm" placeholder="Crie sua nova senha" />

                        <div class="mt-3 rounded-xl border border-gray-100 bg-slate-50 p-3">
                            <div class="flex items-center justify-between gap-2 mb-2">
                                <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Força da senha</span>
                                <span class="text-xs font-semibold" :class="passwordStrength.valid ? 'text-emerald-600' : 'text-gray-500'">
                                    {{ passwordStrength.label }}
                                </span>
                            </div>
                            <div class="h-2 rounded-full bg-white overflow-hidden border border-gray-100">
                                <div class="h-full rounded-full transition-all duration-300" :class="passwordStrength.tone" :style="{ width: passwordStrength.width + '%' }"></div>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                <div
                                    v-for="item in passwordStrength.checks"
                                    :key="item.key"
                                    class="text-xs rounded-lg px-2 py-1.5 border"
                                    :class="item.met ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-500'"
                                >
                                    {{ item.met ? 'OK' : 'Falta' }}: {{ item.label }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Confirmar senha</label>
                        <input v-model="form.confirmPassword" type="password" maxlength="80" class="input input-bordered w-full text-sm" placeholder="Repita a senha" />
                    </div>

                    <div v-if="formError" class="text-red-500 text-xs">{{ formError }}</div>

                    <button
                        type="submit"
                        :disabled="submitting || !passwordStrength.valid"
                        class="mt-1 w-full btn btn-primary"
                    >
                        <svg v-if="submitting" class="animate-spin mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ submitting ? 'Salvando...' : 'Redefinir senha' }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import AuthService from '../services/AuthService';
import { getPasswordStrengthMeta } from '@/utils/password';

export default {
    name: 'ResetPassword',
    setup() {
        const route = useRoute();
        const token = route.params.token;

        const loading = ref(true);
        const submitting = ref(false);
        const error = ref(false);
        const errorMessage = ref('');
        const success = ref(false);
        const resetEmail = ref('');
        const formError = ref('');
        const form = ref({ password: '', confirmPassword: '' });
        const passwordStrength = computed(() => getPasswordStrengthMeta(form.value.password));

        onMounted(async () => {
            try {
                const { data } = await AuthService.validateResetPasswordToken(token);
                resetEmail.value = data.data.email;
            } catch (err) {
                error.value = true;
                const code = err?.response?.data?.error;
                if (code === 'token_expired') {
                    errorMessage.value = 'Este link expirou. Solicite um novo link de redefinição.';
                } else {
                    errorMessage.value = 'Link inválido ou não encontrado.';
                }
            } finally {
                loading.value = false;
            }
        });

        async function submit() {
            formError.value = '';
            if (!passwordStrength.value.valid) {
                formError.value = 'A senha ainda não atende todos os requisitos.';
                return;
            }
            if (form.value.password !== form.value.confirmPassword) {
                formError.value = 'As senhas não coincidem.';
                return;
            }

            try {
                submitting.value = true;
                await AuthService.resetPasswordWithToken(token, form.value.password);
                success.value = true;
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'token_expired') {
                    formError.value = 'Este link expirou. Solicite um novo link de redefinição.';
                } else if (code === 'weak_password') {
                    formError.value = err?.response?.data?.text || 'Senha fora do padrão de segurança.';
                } else {
                    formError.value = 'Erro ao redefinir a senha. Tente novamente.';
                }
            } finally {
                submitting.value = false;
            }
        }

        return {
            loading,
            submitting,
            error,
            errorMessage,
            success,
            resetEmail,
            form,
            formError,
            passwordStrength,
            submit
        };
    }
}
</script>
